import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { useAccountStore } from '@/stores/account'
import { resolveQueueTitle, resolveTicketTitle } from '@/utils/utils'
import Home from '@/views/Home.vue'
import { authGuard } from './authGuard'

// Role-based navigation guard
const roleGuard = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const accountStore = useAccountStore()

  if (to.meta.requiresAdmin && !accountStore.isAdminUser) {
    next({ path: '/' })

    return
  }

  if (to.meta.noCustomers && accountStore.isCustomerUser) {
    next({ path: '/' })

    return
  }

  if (to.meta.noClients && accountStore.isClientUser) {
    next({ path: '/' })

    return
  }

  next()
}

const history = createWebHistory()
const routes = [
  {
    path: '/',
    name: 'Root',
    redirect: '/tickets',
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/home',
    name: 'Home',
    component: Home,
    meta: {
      layout: 'default',
      requiresAuth: true,
    },
  },

  {
    path: '/settings',
    name: 'Settings',
    meta: { requiresAuth: true, requiresAdmin: true, layout: 'default', showBreadcrumb: true },
    redirect: '/settings/ticket',
    children: [
      {
        path: 'ticket',
        name: 'Ticket Configuration',
        component: () => import('@/views/admin/layouts/FormsLayout.vue'),
        meta: { section: 'ticket', type: 'settings', layout: 'default', showBreadcrumb: true },
        children: [
          {
            path: 'types',
            name: 'Ticket Types',
            component: () => import('@/views/admin/components/type/TicketTypeForm.vue'),
          },
          {
            path: 'statuses',
            name: 'Ticket Statuses',
            component: () => import('@/views/admin/components/status/TicketStatusForm.vue'),
          },
          {
            path: 'priorities',
            name: 'Ticket Priorities',
            component: () => import('@/views/admin/components/priority/TicketPriorityForm.vue'),
          },
          {
            path: 'categories',
            name: 'Ticket Categories',
            component: () => import('@/views/admin/components/category/TicketCategoryForm.vue'),
          },
        ],
      },

      {
        path: 'queue',
        name: 'Queue Configuration',
        meta: { section: 'queue', type: 'settings', layout: 'default', showBreadcrumb: true },
        component: () => import('@/views/admin/layouts/FormsLayout.vue'),
        children: [
          {
            path: ':id',
            name: 'QueueSettings',
            component: () => import('@/views/admin/components/queue/TicketQueueForm.vue'),
            props: true,
            meta: { section: 'queue' },
          },
        ],
      },

      // ──────────────── Module Team ────────────────
      {
        path: 'team',
        name: 'Teams Configuration',
        component: () => import('@/views/admin/layouts/FormsLayout.vue'),
        meta: { section: 'team', type: 'settings', layout: 'default', showBreadcrumb: true },
        children: [
          {
            path: 'users',
            name: 'Users',
            component: () => import('@/views/admin/components/user/TicketUserForm.vue'),
          },
          {
            path: 'teams',
            name: 'Teams',
            component: () => import('@/views/admin/components/team/TicketTeamForm.vue'),
          },
        ],
      },
      {
        path: 'workflow',
        name: 'Workflow',
        component: () => import('@/views/admin/components/workflow/TicketWorkflowForm.vue'),
        meta: { section: 'workflow', layout: 'default', showBreadcrumb: true },
        children: [],
      },
      {
        path: 'queues',
        name: 'Settings Queue',
        component: () => import('@/views/admin/layouts/FormsLayout.vue'),
        meta: { section: 'queue', type: 'settings', layout: 'default', showBreadcrumb: true },
        children: [],
      },
      {
        path: 'sla',
        name: 'SLA Configuration',
        component: () => import('@/views/admin/layouts/FormsLayout.vue'),
        meta: { section: 'sla', type: 'settings', layout: 'default', showBreadcrumb: true },
        children: [
          {
            path: '',
            redirect: 'slas',
          },
          {
            path: 'slas',
            name: 'SLAs',
            component: () => import('@/views/admin/components/sla/TicketSlaForm.vue'),
          },
          {
            path: 'calendars',
            name: 'Calendars',
            component: () => import('@/views/admin/components/sla/CalendarForm.vue'),
          },
        ],
      },
    ],
  },

  {
    path: '/queues/:queueSlug',
    name: 'QueueView',
    component: () => import('@/views/tickets/Queue.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: true,
      title: resolveQueueTitle,
      breadcrumb: resolveQueueTitle,
      showBreadcrumb: true,
    },
  },
  {
    path: '/tickets',
    name: 'Ticket',
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: true,
      breadcrumb: 'Help Desk',
      showBreadcrumb: true,
    },
    children: [
      {
        path: '',
        name: 'Tickets',
        meta: {
          breadcrumb: 'Tickets',
        },
        component: () => import('@/views/tickets/Queue.vue'),
      },
      {
        path: ':ticketId',
        name: 'TicketDetails',
        component: () => import('@/views/tickets/components/TicketView.vue'),
        meta: {
          layout: 'default',
          requiresAuth: true,
          showTitle: false,
          title: resolveTicketTitle,
          breadcrumb: resolveTicketTitle,
        },
      },
    ],
  },

  {
    path: '/insights/clients',
    name: 'Clients Insights',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import('@/views/insights/Client.vue'),
    meta: {
      layout: 'default',
      title: 'Insights Summary',
      requiresAuth: true,
      showTitle: true,
      showBreadcrumb: true,
    },
  },
  {
    path: '/insights/tickets',
    name: 'Tickets Insights',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import('@/views/insights/Ticket.vue'),
    meta: {
      layout: 'default',
      title: 'Insights Summary',
      requiresAuth: true,
      showTitle: true,
      showBreadcrumb: true,
      noCustomers: true,
      noClients: true,
    },
  },
  {
    path: '/knowledge',
    name: 'Notebook',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import('@/views/Notebook.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: true,
      showBreadcrumb: true,
    },
  },
  {
    path: '/projects',
    name: 'ProjectsList',
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: true,
      showBreadcrumb: true,
      breadcrumb: 'Projects',
    },
    children: [
      {
        path: '',
        name: 'Projects',
        meta: {
          breadcrumb: 'Projects',
        },
        component: () => import('@/views/boards/Projects.vue'),
      },
      {
        path: ':projectId',
        name: 'ProjectDetails',
        component: () => import('@/views/boards/ProjectDetails.vue'),
        props: true,
        meta: {
          layout: 'default',
          requiresAuth: true,
          showTitle: false,
          showBreadcrumb: false,
          breadcrumb: 'Project Details',
        },
      },
      {
        path: 'current',
        name: 'CurrentProjects',
        meta: {
          breadcrumb: 'Current Projects',
        },
        component: () => import('@/views/boards/KanbanBoard.vue'),
      },
    ],
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/views/Notifications.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: false,
      showBreadcrumb: true,
    },
  },
  {
    path: '/notifications/settings',
    name: 'NotificationSettings',
    component: () => import('@/views/NotificationSettings.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: false,
      showBreadcrumb: true,
    },
  },
  {
    path: '/inbox',
    name: 'Desk',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import('@/views/inbox/Desk.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: true,
      showBreadcrumb: true,
    },
  },
  {
    path: '/companies',
    name: 'Companies',
    component: () => import('@/views/companies/Companies.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: true,
      showBreadcrumb: true,
      noClients: true,
    },
  },
  {
    path: '/risk-table',
    name: 'RiskTable',
    component: () => import('@/views/risk/RiskTable.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      requiresAdmin: true,
      showTitle: true,
      showBreadcrumb: true,
      breadcrumb: 'Risk Table',
    },
  },
  {
    path: '/risk-settings',
    name: 'RiskSettings',
    component: () => import('@/views/risk/RiskSettings.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      requiresAdmin: true,
      showTitle: true,
      showBreadcrumb: true,
      breadcrumb: 'Risk Settings',
    },
  },
  {
    path: '/customers',
    name: 'Customers',
    component: () => import('@/views/customers/Customers.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: true,
      showBreadcrumb: true,
      noClients: true,
    },
  },
  {
    path: '/customers/:customerId',
    name: 'CustomerDetails',
    component: () => import('@/views/customers/CustomerDetails.vue'),
    props: true,
    meta: {
      layout: 'default',
      requiresAuth: true,
      showTitle: false,
      showBreadcrumb: true,
      noClients: true,
      breadcrumb: 'Overview',
    },
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('@/views/auth/Callback.vue'),
    meta: { layout: 'login', requiresAuth: false },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      layout: 'default',
      requiresAuth: true,
    },
  },
]
const router = createRouter({
  linkActiveClass: 'active',
  history,
  routes,
})

router.beforeEach(authGuard)
router.beforeEach(roleGuard)

export { router }
